import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import complaintsService from "../services/complaintsService";
import { downloadPDF } from "../utils/exportUtils";
import "../assets/users.css";
import "../assets/panels.css";
import Loader from "../components/Loader";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

// Add CSS styles for the new complaints interface
const complaintsStyles = `
  .complaints-overview {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-bottom: 20px;
  }
  
  .complaints-overview h2 {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 20px;
    color: #333;
  }
  
  .status-cards-container {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }
  
  .status-card {
    flex: 1;
    min-width: 150px;
    padding: 20px;
    border-radius: 8px;
    text-align: center;
    color: white;
    font-weight: bold;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: transform 0.2s;
  }
  
  .status-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
  
  .status-card.request { background-color: #6c757d; }
  .status-card.in-action { background-color: #007bff; }
  .status-card.contractor-completed { background-color: #28a745; }
  .status-card.completed { background-color: #20c997; }
  .status-card.all-complaints { background-color: #dc3545; }
  
  .status-number {
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 8px;
  }
  
  .status-label {
    font-size: 0.9rem;
    opacity: 0.9;
  }
  
  .calendar-dropdown {
    margin-top: 15px;
  }
  
  .calendar-dropdown label {
    font-weight: bold;
    margin-right: 10px;
  }
  
  .calendar-dropdown select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    min-width: 200px;
  }
  
  .complaints-filters {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-bottom: 20px;
  }
  
  .complaints-filters h2 {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 20px;
    color: #333;
  }
  
  .filters-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
  
  .filter-group {
    margin-bottom: 15px;
  }
  
  .filter-group label {
    display: block;
    font-weight: bold;
    margin-bottom: 5px;
    color: #333;
  }
  
  .filter-group input,
  .filter-group select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
  }
  
  .filter-group .date-range {
    display: flex;
    gap: 10px;
  }
  
  .filter-group .date-range input {
    flex: 1;
  }
  
  .filter-group .date-range input[type="date"] {
    min-width: 100px; /* Ensure date inputs have a minimum width */
  }
  
  .filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
  
  .btn-show-certificates {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
  }
  
  .btn-search {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
  }
  
  .btn-delete-selected {
    background-color: #dc3545;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
  }
  
  .btn-show-certificates:hover,
  .btn-search:hover {
    background-color: #0056b3;
  }
  
  .btn-delete-selected:hover {
    background-color: #c82333;
  }
  
  .complaints-table {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  
  .table-title {
    font-size: 1.5rem;
    font-weight: bold;
    color: #333;
  }
  
  .table-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  
  .table-actions input[type="text"] {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    min-width: 200px;
  }
  
  .table-actions select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  
  .table-actions button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
  }
  
  .btn-export {
    background-color: #28a745;
    color: white;
  }
  
  .btn-export:hover {
    background-color: #218838;
  }
  
  .btn-columns {
    background-color: #6c757d;
    color: white;
  }
  
  .btn-columns:hover {
    background-color: #5a6268;
  }
  
  .column-dropdown {
    position: relative;
  }
  
  .column-dropdown-content {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
    min-width: 200px;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .column-dropdown-content label {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    cursor: pointer;
  }
  
  .column-dropdown-content input[type="checkbox"] {
    margin-right: 8px;
  }
  
  .table-container {
    overflow-x: auto;
  }
  
  .table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
  }
  
  .table th {
    background-color: #f8f9fa;
    padding: 12px 8px;
    text-align: left;
    font-weight: bold;
    color: #333;
    border-bottom: 2px solid #dee2e6;
    position: relative;
    cursor: pointer;
  }
  
  .table th:hover {
    background-color: #e9ecef;
  }
  
  .table th .sort-icon {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 12px;
  }
  
  .table td {
    padding: 12px 8px;
    border-bottom: 1px solid #dee2e6;
    vertical-align: middle;
  }
  
  .table tbody tr:hover {
    background-color: #f8f9fa;
  }
  
  .table tbody tr.selected {
    background-color: #e3f2fd;
  }
  
  .checkbox-cell {
    width: 40px;
    text-align: center;
  }
  
  .checkbox-cell input[type="checkbox"] {
    margin: 0;
  }
  
  .status-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
  }
  
  .status-badge.request { background-color: #6c757d; color: white; }
  .status-badge.in-action { background-color: #007bff; color: white; }
  .status-badge.contractor-completed { background-color: #28a745; color: white; }
  .status-badge.completed { background-color: #20c997; color: white; }
  .status-badge.closed { background-color: #6c757d; color: white; }
  
  .priority-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
  }
  
  .priority-badge.high { background-color: #dc3545; color: white; }
  .priority-badge.medium { background-color: #ffc107; color: #212529; }
  .priority-badge.low { background-color: #28a745; color: white; }
  
  .action-buttons {
    display: flex !important;
    flex-direction: row !important;
    gap: 5px !important;
    align-items: center !important;
    justify-content: center !important;
    flex-wrap: nowrap !important;
  }

  .btn-show,
  .btn-edit,
  .btn-delete {
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: bold;
    display: inline-block !important;
    width: auto !important;
    margin: 0 !important;
  }
  
  .btn-show {
    background-color: #007bff;
    color: white;
  }
  
  .btn-edit {
    background-color: #ffc107;
    color: #212529;
  }
  
  .btn-delete {
    background-color: #dc3545;
    color: white;
  }
  
  .btn-show:hover { background-color: #0056b3; }
  .btn-edit:hover { background-color: #e0a800; }
  .btn-delete:hover { background-color: #c82333; }
  
  .pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding: 20px 0;
  }
  
  .pagination-info {
    color: #6c757d;
  }
  
  .pagination-controls {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  
  .pagination-controls button {
    padding: 8px 12px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .pagination-controls button:hover {
    background-color: #f8f9fa;
  }
  
  .pagination-controls button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination-controls select {
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  
  .no-data {
    text-align: center;
    padding: 40px;
    color: #6c757d;
    font-style: italic;
  }
  
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
`;

// Define all columns for complaints table
const allColumns = [
  { key: "submitted", label: "Submitted" },
  { key: "time", label: "Time" },
  { key: "status", label: "Status" },
  { key: "deadline", label: "Deadline" },
  { key: "book_in", label: "Book in" },
  { key: "completed", label: "Completed" },
  { key: "job_type", label: "Job type" },
  { key: "property_name", label: "Property Name" },
  { key: "postcode", label: "Postcode" },
  { key: "assigned", label: "Assigned" },
  { key: "to_delete", label: "To delete" },
  { key: "actions", label: "Actions" },
];

const STATUS_CARDS = [
  { key: "request", label: "REQUEST", color: "#6c757d", className: "request" },
  {
    key: "in_action",
    label: "IN ACTION",
    color: "#007bff",
    className: "in-action",
  },
  {
    key: "contractor_completed",
    label: "CONTRACTOR COMPLETED",
    color: "#28a745",
    className: "contractor-completed",
  },
  {
    key: "completed",
    label: "COMPLETED",
    color: "#20c997",
    className: "completed",
  },
  {
    key: "all_complaints",
    label: "ALL COMPLAINTS",
    color: "#dc3545",
    className: "all-complaints",
  },
];

const Complaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50);
  const [statusOverview, setStatusOverview] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((col) => col.key).filter((key) => key !== "to_delete")
  );
  const [showColDropdown, setShowColDropdown] = useState(false);
  const [selectedComplaints, setSelectedComplaints] = useState(new Set());
  const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);
  const [filterData, setFilterData] = useState({
    jobTypes: [],
    propertyAreas: [],
    clients: [],
    priorities: [],
    contractors: [],
    users: [],
  });
  const [filters, setFilters] = useState({
    submitted_date_from: "",
    submitted_date_to: "",
    job_type: "",
    property_area: "",
    client: "",
    contractor: "",
  });
  const [selectedUser, setSelectedUser] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedStatusCard, setSelectedStatusCard] = useState("");
  const navigate = useNavigate();

  // State for sorting
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // State for server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  const handleStatusCardClick = (statusKey) => {
    console.log('🔍 Complaint status card clicked:', statusKey);
    const newFilter = statusFilter === statusKey ? '' : statusKey;
    setStatusFilter(newFilter);
    setSelectedStatusCard(newFilter);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    loadStatusOverview();
    loadFilterData();
    loadComplaints();
  }, []);

  const loadStatusOverview = async () => {
    try {
      const overview = await complaintsService.getStatusOverview();
      console.log("Status overview:", overview);
      setStatusOverview(overview);
    } catch (err) {
      console.error("Error loading status overview:", err);
      setStatusOverview({});
    }
  };

  const loadFilterData = async () => {
    try {
      const data = await complaintsService.getFilterData();
      console.log("Filter data:", data);
      setFilterData(data);
    } catch (err) {
      console.error("Error loading filter data:", err);
      setFilterData({
        jobTypes: [],
        propertyAreas: [],
        clients: [],
        priorities: [],
        contractors: [],
        users: [],
      });
    }
  };

  const loadComplaints = async (page = currentPage, limit = entries) => {
    setLoading(true);
    try {
      console.log("🔍 [FRONTEND] Loading complaints with filters:", filters, "page:", page, "limit:", limit);
      const result = await complaintsService.getComplaints(filters, page, limit);
      console.log("🔍 [FRONTEND] Raw complaints data received:", result);

      if (result && result.data && Array.isArray(result.data)) {
        console.log("🔍 [FRONTEND] Setting complaints from result.data, length:", result.data.length);
        setComplaints(result.data);

        // Update pagination state
        if (result.pagination) {
          setCurrentPage(result.pagination.current_page);
          setTotalItems(result.pagination.total);
          setLastPage(result.pagination.last_page);
          console.log("🔍 [FRONTEND] Pagination updated:", result.pagination);
        }
      } else {
        console.error("🔍 [FRONTEND] Unexpected data format from getComplaints:", result);
        setComplaints([]);
        setError("Unexpected data format received from server");
      }
    } catch (err) {
      console.error("🔍 [FRONTEND] Error loading complaints:", err);
      setComplaints([]);
      setError(err.message || "Failed to load complaints");
    } finally {
      setLoading(false);
    }
  };

  // Use backend status overview for accurate total counts (not just current page)
  const statusCounts = useMemo(() => {
    // Map backend response to card keys
    return {
      all_complaints: statusOverview.total || 0,
      request: statusOverview.request || 0,
      in_action: statusOverview.in_action || 0,
      contractor_completed: statusOverview.contractor_completed || 0,
      completed: statusOverview.completed || 0
    };
  }, [statusOverview]);

  const getExportRows = () => {
    return complaints.map((complaint) => ({
      Submitted: complaint.created_at
        ? new Date(complaint.created_at).toLocaleDateString()
        : "N/A",
      Time: complaint.created_at
        ? new Date(complaint.created_at).toLocaleTimeString()
        : "N/A",
      Status: complaint.status_name || complaint.status || "N/A",
      Deadline: complaint.deadline
        ? new Date(complaint.deadline).toLocaleDateString()
        : "N/A",
      "Book in": complaint.book_in_date
        ? new Date(complaint.book_in_date).toLocaleDateString()
        : "N/A",
      Completed: complaint.completed_date
        ? new Date(complaint.completed_date).toLocaleDateString()
        : "N/A",
      "Job Type": complaint.job_type || "N/A",
      "Property Name": complaint.property_name || "N/A",
      Postcode: complaint.postcode || "N/A",
      Assigned: complaint.assigned_member || "N/A",
      Guardian: complaint.guardian_name || complaint.guardian_number || "N/A",
      Description: complaint.description || "N/A",
    }));
  };

  const handleExportCSV = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      Object.keys(getExportRows()[0]).join(",") +
      "\n" +
      getExportRows()
        .map((row) => Object.values(row).join(","))
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "complaints.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    try {
      console.log('Starting PDF export for complaints...');
      console.log('Filtered complaints:', filteredComplaints);

      // Transform data for PDF export
      const pdfData = filteredComplaints.map((complaint) => ({
        submitted: complaint.created_at
          ? new Date(complaint.created_at).toLocaleDateString()
          : "N/A",
        time: complaint.created_at
          ? new Date(complaint.created_at).toLocaleTimeString()
          : "N/A",
        status: complaint.status_name || complaint.status || "N/A",
        deadline: complaint.deadline
          ? new Date(complaint.deadline).toLocaleDateString()
          : "N/A",
        book_in: complaint.book_in_date
          ? new Date(complaint.book_in_date).toLocaleDateString()
          : "N/A",
        completed: complaint.completed_date
          ? new Date(complaint.completed_date).toLocaleDateString()
          : "N/A",
        job_type: complaint.job_type || "N/A",
        property_name: complaint.property_name || "N/A",
        postcode: complaint.postcode || "N/A",
        assigned: complaint.assigned_member || "N/A",
        guardian: complaint.guardian_name || complaint.guardian_number || "N/A",
        description: complaint.description || "N/A",
      }));

      // Get visible columns (excluding actions and to_delete)
      const columnsToExport = visibleColumns.filter(col => col !== 'actions' && col !== 'to_delete');

      console.log('Columns to export:', columnsToExport);
      console.log('PDF data sample:', pdfData.slice(0, 2));

      downloadPDF(
        pdfData,
        "complaints_report.pdf",
        columnsToExport,
        "Complaints Report",
        {
          enableMultiPage: true,
          fontSize: 8,
          orientation: 'landscape'
        }
      );

      console.log('PDF export completed successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF: ' + error.message);
    }
  };

  const handleColDropdownToggle = () => setShowColDropdown((v) => !v);
  const handleColDropdownBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setShowColDropdown(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = async () => {
    setCurrentPage(1); // Reset to first page on search
    await loadComplaints(1, entries);
  };

  // Handle page change
  const handlePageChange = async (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      setCurrentPage(newPage);
      await loadComplaints(newPage, entries);
    }
  };

  // Handle entries per page change
  const handleEntriesChange = async (newEntries) => {
    setEntries(newEntries);
    setCurrentPage(1); // Reset to first page when changing page size
    await loadComplaints(1, newEntries);
  };

  const handleShowCertificates = () => {
    navigate("/certificates");
  };

  const handleDeleteSelected = async () => {
    if (selectedComplaints.size === 0) {
      alert("Please select complaints to delete");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedComplaints.size} complaint(s)?`
      )
    ) {
      return;
    }

    setDeleteSelectedLoading(true);
    try {
      await complaintsService.bulkDeleteComplaints(
        Array.from(selectedComplaints)
      );
      setSelectedComplaints(new Set());
      await loadComplaints();
      await loadStatusOverview();
    } catch (err) {
      console.error("Error deleting complaints:", err);
      alert("Failed to delete complaints");
    } finally {
      setDeleteSelectedLoading(false);
    }
  };

  const handleComplaintSelection = (complaintId, isChecked) => {
    const newSelected = new Set(selectedComplaints);
    if (isChecked) {
      newSelected.add(complaintId);
    } else {
      newSelected.delete(complaintId);
    }
    setSelectedComplaints(newSelected);
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedComplaints(new Set(complaints.map((c) => c.id)));
    } else {
      setSelectedComplaints(new Set());
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Are you sure you want to delete this complaint?")) {
      return;
    }

    try {
      await complaintsService.deleteComplaint(id);
      await loadComplaints();
      await loadStatusOverview();
    } catch (err) {
      console.error("Error deleting complaint:", err);
      alert("Failed to delete complaint");
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    const statusLower = status.toLowerCase();

    if (statusLower.includes("request"))
      return <span className="status-badge request">Request</span>;
    if (statusLower.includes("action"))
      return <span className="status-badge in-action">In Action</span>;
    if (statusLower.includes("contractor"))
      return (
        <span className="status-badge contractor-completed">
          Contractor Completed
        </span>
      );
    if (statusLower.includes("completed") || statusLower.includes("closed"))
      return <span className="status-badge completed">Completed</span>;

    return <span className="status-badge request">{status}</span>;
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return null;
    const priorityLower = priority.toLowerCase();

    if (priorityLower === "high")
      return <span className="priority-badge high">High</span>;
    if (priorityLower === "medium")
      return <span className="priority-badge medium">Medium</span>;
    if (priorityLower === "low")
      return <span className="priority-badge low">Low</span>;

    return <span className="priority-badge medium">{priority}</span>;
  };

  // Since complaints are stored in tickets table with is_complaint=1,
  // the complaint.id IS already the ticket_id
  const handleShowComplaint = (complaintId) => {
    console.log("🔍 [COMPLAINTS] Navigating to show ticket:", complaintId);
    navigate(`/tickets/${complaintId}`);
  };

  const handleEditComplaint = (complaintId) => {
    console.log("🔍 [COMPLAINTS] Navigating to edit ticket:", complaintId);
    navigate(`/tickets/${complaintId}/edit`);
  };

  // Filter and paginate complaints
  const filteredComplaints = useMemo(() => {
    if (!Array.isArray(complaints)) return [];

    let filtered = complaints;

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (complaint) =>
          (complaint.description &&
            complaint.description.toLowerCase().includes(searchLower)) ||
          (complaint.guardian_name &&
            complaint.guardian_name.toLowerCase().includes(searchLower)) ||
          (complaint.guardian_number &&
            complaint.guardian_number.toLowerCase().includes(searchLower)) ||
          (complaint.property_name &&
            complaint.property_name.toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter (matching Laravel logic with status_id)
    if (statusFilter && statusFilter !== 'all_complaints') {
      filtered = filtered.filter(complaint => {
        const statusId = complaint.status_id;
        
        switch (statusFilter) {
          case 'request':
            return statusId === 1;
          case 'in_action':
            return statusId === 2;
          case 'contractor_completed':
            return statusId === 7;
          case 'completed':
            return statusId === 5;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting
      if (sortField === "property") {
        aValue = a.property_name || "";
        bValue = b.property_name || "";
      } else if (sortField === "guardian") {
        aValue = a.guardian_name || "";
        bValue = b.guardian_name || "";
      } else if (sortField === "status") {
        aValue = a.status_name || "";
        bValue = b.status_name || "";
      } else if (sortField === "created_at") {
        aValue = new Date(a.created_at || 0);
        bValue = new Date(b.created_at || 0);
      } else if (sortField === "updated_at") {
        aValue = new Date(a.updated_at || 0);
        bValue = new Date(b.updated_at || 0);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [complaints, search, statusFilter, sortField, sortDirection]);

  // With server-side pagination, data is already paginated from API
  // Client-side filtering for search and status is still applied
  const paginatedComplaints = useMemo(() => {
    return filteredComplaints;
  }, [filteredComplaints]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  // Format time for display
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  // Inject styles
  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = complaintsStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  if (loading && complaints.length === 0) {
    return <Loader />;
  }

  return (
    <div className="container-fluid">
      {/* Breadcrumbs */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/dashboard">Dashboard</a>
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Complaints</span>
        </div>
      </div>

      {/* Status Overview */}
      <div
        className="panel-heading panel-title"
        style={{ margin: "0", border: "none" }}
      >
        <h3 className="mr-auto">Activity Overview</h3>
      </div>
      <div className="panel-body">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          {STATUS_CARDS.map((card) => (
            <div className="ticket-overview-container" key={card.key}>
              <div className="ticket-overview-row">
                <div
                  className={`ticket-overview-card ${selectedStatusCard === card.key ? 'active' : 'all-active'}`}
                  style={{
                    background: card.color,
                    cursor: 'pointer',
                    transform: selectedStatusCard === card.key ? 'scale(1.05)' : 'scale(1)',
                    border: selectedStatusCard === card.key ? '3px solid #fff' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => handleStatusCardClick(card.key)}
                  title={`Filter by ${card.label} status`}
                >
                  <div className="ticket-overview-value">
                    {statusCounts[card.key] || 0}
                  </div>
                  <div
                    className="ticket-overview-label  text-white overview-label"
                    style={{ textAlign: "center", color: "white" }}
                  >
                    {card.label}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="calendar-dropdown ticket_goto_calendar">
        <label>Go to user calendar:</label>
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <option value="">Please select</option>
          {filterData.users?.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </div>

      {/* Filters */}
      <div className="complaints-filters">
        <div className="tickets-maintenance-header">
          <b>Complaints</b>
        </div>
        <div className="ticket_date_ticket_wrapper">
          <div className="ticket_date_ticket">
            <b style={{ width: "240px" }}>SUBMITTED DATE</b>
            <div className="ticket_date_ticket_from">
              <b>From</b>
              <input
                className="form-control"
                type="date"
                value={filters.submitted_date_from}
                onChange={(e) =>
                  handleFilterChange("submitted_date_from", e.target.value)
                }
              />
            </div>
            <div className="ticket_date_ticket_to">
              <b>To</b>
              <input
                className="form-control"
                type="date"
                value={filters.submitted_date_to}
                onChange={(e) =>
                  handleFilterChange("submitted_date_to", e.target.value)
                }
              />
            </div>
          </div>
          <div className="ticket_date_ticket" style={{ marginTop: "10px" }}>
            <b style={{ width: "240px" }}>EXPIRY DATE</b>
            <div className="ticket_date_ticket_from">
              <b>From</b>
              <input
                className="form-control"
                type="date"
                value={filters.date_estimated_completion_from}
                onChange={(e) =>
                  handleFilterChange(
                    "date_estimated_completion_from",
                    e.target.value
                  )
                }
              />
            </div>
            <div className="ticket_date_ticket_to">
              <b>To</b>
              <input
                className="form-control"
                type="date"
                value={filters.date_estimated_completion_to}
                onChange={(e) =>
                  handleFilterChange(
                    "date_estimated_completion_to",
                    e.target.value
                  )
                }
              />
            </div>
          </div>
          <br />
          <div style={{ display: "flex" }}>
            <div
              className="ticket_date_ticket_job_type"
              style={{ marginTop: "10px" }}
            >
              <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>
                Job Type
              </b>
              <div>
                <select
                  className="form-control"
                  style={{ width: "274px" }}
                  value={filters.type_id}
                  onChange={(e) =>
                    handleFilterChange("job_type", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {filterData.jobTypes?.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div
              className="ticket_date_ticket_job_type"
              style={{ marginTop: "10px" }}
            >
              <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>
                Priority
              </b>
              <div>
                <select
                  className="form-control"
                  style={{ width: "150px", marginRight: "100px" }}
                  value={filters.urgency_id}
                  onChange={(e) =>
                    handleFilterChange("urgency_id", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {filterData.urgencies?.map((urgency) => (
                    <option key={urgency.id} value={urgency.id}>
                      {urgency.name}
                    </option>
                  )) || []}
                </select>
              </div>
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div
              className="ticket_date_ticket_job_type"
              style={{ marginTop: "10px" }}
            >
              <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>
                Property Area
              </b>
              <div>
                <select
                  className="form-control"
                  style={{ width: "150px", marginRight: "125px" }}
                  value={filters.location_id}
                  onChange={(e) =>
                    handleFilterChange("property_area", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {filterData.propertyAreas?.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div
              className="ticket_date_ticket_job_type"
              style={{ marginTop: "10px" }}
            >
              <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>
                Contrator
              </b>
              <div>
                <select
                  className="form-control"
                  style={{ width: "250px" }}
                  value={filters.contractor_id}
                  onChange={(e) =>
                    handleFilterChange("contractor", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {filterData.contractors?.map((contractor) => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.name}
                    </option>
                  )) || []}
                </select>
              </div>
            </div>
          </div>

          {/* start */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              className="ticket_date_ticket_job_type"
              style={{ marginTop: "10px" }}
            >
              <b style={{ width: "240px", fontSize: "14px", fontWeight: 700 }}>
                Client
              </b>
              <div>
                <input
                  className="form-control"
                  style={{ width: "250px", marginRight: "25px" }}
                  placeholder="Type client name"
                  value={filters.client_id}
                  onChange={(e) => handleFilterChange("client", e.target.value)}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "8px",
                marginLeft: "auto",
              }}
            >
              <button
                style={{ background: "#337AB7" }}
                type="button"
                className="btn-action certificates_show"
                onClick={handleShowCertificates}
              >
                Show Certificates
              </button>
              <button
                style={{ background: "#337AB7" }}
                type="button"
                className="btn-action search_edit"
                onClick={handleSearch}
              >
                Search
              </button>
              {selectedComplaints.size > 0 && (
                <span
                  style={{
                    marginRight: "10px",
                    fontSize: "14px",
                    color: "#666",
                    fontWeight: "500",
                  }}
                >
                  {selectedComplaints.size} selected
                </span>
              )}
              <button
                style={{ background: "#D9534F" }}
                type="button"
                className="btn-action ticket_remove_delete"
                onClick={handleDeleteSelected}
                disabled={
                  deleteSelectedLoading || selectedComplaints.size === 0
                }
              >
                {deleteSelectedLoading ? "Deleting..." : "Delete selected"}
              </button>
            </div>
          </div>
          {/* end */}
        </div>
      </div>

      {/* Complaints Table */}
      <div className="users-table-panel">
        <div className="users-table-toolbar">
          <div className="users-table-entries">
            Show
            <select
              value={entries}
              onChange={(e) => handleEntriesChange(Number(e.target.value))}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            entries
          </div>
          <div className="users-table-search ticket_search">
            Search:
            <input
              type="text"
              className="form-control"
              placeholder="Search tickets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            id="btn-export-csv-property"
            className="btn"
            onClick={handleExportCSV}
          >
            Export as CSV
          </button>
          <button
            id="btn-pdf-export-property"
            className="btn"
            onClick={handleExportPDF}
          >
            Export as PDF
          </button>
          <button
            id="btn-visibility-property"
            className="btn"
            onClick={handleColDropdownToggle}
            tabIndex={0}
            style={{ minWidth: 120 }}
          >
            Column visibility
          </button>
          <div className="table-actions">
            <div className="column-dropdown" onBlur={handleColDropdownBlur}>
              {showColDropdown && (
                <div className="column-dropdown-content">
                  {allColumns.map((col) => (
                    <label key={col.key}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setVisibleColumns((prev) => [...prev, col.key]);
                          } else {
                            setVisibleColumns((prev) =>
                              prev.filter((c) => c !== col.key)
                            );
                          }
                        }}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                {allColumns.map(
                  (col) =>
                    visibleColumns.includes(col.key) && (
                      <th 
                        key={col.key} 
                        className={`text-nowrap ${col.key !== "to_delete" ? "sortable" : ""}`}
                        onClick={col.key !== "to_delete" ? () => handleSort(col.key) : undefined}
                        style={{ cursor: col.key !== "to_delete" ? "pointer" : "default" }}
                      >
                        {col.key === "to_delete" ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                selectedComplaints.size > 0 &&
                                selectedComplaints.size ===
                                  paginatedComplaints.length
                              }
                              onChange={(e) =>
                                handleSelectAll(e.target.checked)
                              }
                            />
                            {col.label}
                          </div>
                        ) : (
                          <>
                            {col.label}<SortIcon direction={sortField === col.key ? sortDirection : null} isActive={sortField === col.key} />
                          </>
                        )}
                      </th>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedComplaints.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="no-data">
                    No complaints found matching your criteria.
                  </td>
                </tr>
              ) : (
                paginatedComplaints.map((complaint) => (
                  <tr
                    key={complaint.id}
                    className={
                      selectedComplaints.has(complaint.id) ? "selected" : ""
                    }
                  >
                    {visibleColumns.includes("submitted") && (
                      <td>{formatDate(complaint.created_at)}</td>
                    )}
                    {visibleColumns.includes("time") && (
                      <td>{formatTime(complaint.created_at)}</td>
                    )}
                    {visibleColumns.includes("status") && (
                      <td>
                        {getStatusBadge(
                          complaint.status_name || complaint.status
                        )}
                      </td>
                    )}
                    {visibleColumns.includes("deadline") && (
                      <td>{formatDate(complaint.deadline)}</td>
                    )}
                    {visibleColumns.includes("book_in") && (
                      <td>{formatDate(complaint.book_in_date)}</td>
                    )}
                    {visibleColumns.includes("completed") && (
                      <td>{formatDate(complaint.completed_date)}</td>
                    )}
                    {visibleColumns.includes("job_type") && (
                      <td>{complaint.job_type || "N/A"}</td>
                    )}
                    {visibleColumns.includes("property_name") && (
                      <td>{complaint.property_name || "N/A"}</td>
                    )}
                    {visibleColumns.includes("postcode") && (
                      <td>{complaint.postcode || "N/A"}</td>
                    )}
                    {visibleColumns.includes("assigned") && (
                      <td>{complaint.assigned_member || "N/A"}</td>
                    )}
                    {visibleColumns.includes("to_delete") && (
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selectedComplaints.has(complaint.id)}
                          onChange={(e) =>
                            handleComplaintSelection(
                              complaint.id,
                              e.target.checked
                            )
                          }
                        />
                      </td>
                    )}
                    {visibleColumns.includes("actions") && (
                      <td className="text-center">
                        <div
                          className="action-buttons"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "5px",
                            alignItems: "center",
                            justifyContent: "center",
                            flexWrap: "nowrap",
                            width: "100%"
                          }}
                        >
                          <button
                            className="btn-show"
                            onClick={() => handleShowComplaint(complaint.id)}
                          >
                            Show
                          </button>
                          <button
                            className="btn-edit"
                            onClick={() => handleEditComplaint(complaint.id)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleRemove(complaint.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Showing {paginatedComplaints.length > 0 ? ((currentPage - 1) * entries) + 1 : 0} to{" "}
            {Math.min(currentPage * entries, totalItems)} of {totalItems} complaints
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1 || loading}
            >
              First
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              Previous
            </button>
            <span>Page {currentPage} of {lastPage}</span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= lastPage || loading}
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(lastPage)}
              disabled={currentPage >= lastPage || loading}
            >
              Last
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Complaints;
